import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Divider,
  Paper,
  Stack,
  Grid,
  Chip,
  Button,
} from "@mui/material";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import GraphicEqRoundedIcon from "@mui/icons-material/GraphicEqRounded";
import TipsAndUpdatesRoundedIcon from "@mui/icons-material/TipsAndUpdatesRounded";
import ElectricBoltRoundedIcon from "@mui/icons-material/ElectricBoltRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import CompareArrowsRoundedIcon from "@mui/icons-material/CompareArrowsRounded";
import LibraryMusicRoundedIcon from "@mui/icons-material/LibraryMusicRounded";
import { Link as RouterLink } from "react-router-dom";
import { useStyle } from "../../context/styleContext/styleContext";
import Seo from "../../components/Seo";

export default function SaturationLearning() {
  const { style } = useStyle();
  const [height, setHeight] = useState(window.innerHeight);
  const topContainer = useRef(null);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    topContainer.current?.scrollIntoView({ block: "start" });
  }, []);

  useEffect(() => {
    function handleResize() {
      setHeight(window.innerHeight);
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const accent = "rgb(88, 166, 124)";
  const panelBg = "rgba(255,255,255,0.04)";
  const darkPanel = "rgba(0,0,0,0.18)";

  const articleSchema = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "Article",
      headline: "Saturation in Music Production: What It Is and How It Works",
      description:
        "Learn what saturation is, how it adds harmonics, the difference between tape, tube, transistor, and digital saturation, and how saturation is used in mixing, mastering, and sound design.",
      author: {
        "@type": "Organization",
        name: "Parish Audio",
      },
      publisher: {
        "@type": "Organization",
        name: "Parish Audio",
        logo: {
          "@type": "ImageObject",
          url: "https://parishaudio.com/assets/logo.png",
        },
      },
      mainEntityOfPage: "https://parishaudio.com/learning/saturation",
      articleSection: "Audio Education",
      keywords: [
        "saturation",
        "audio saturation",
        "tape saturation",
        "tube saturation",
        "harmonic saturation",
        "mix saturation",
        "what is saturation in music",
      ],
    }),
    []
  );

  const quickFacts = [
    {
      title: "What it does",
      text: "Saturation adds harmonic content and gentle nonlinear coloration, making sounds feel warmer, thicker, denser, smoother, or more forward.",
      icon: <ElectricBoltRoundedIcon sx={{ color: accent }} />,
    },
    {
      title: "Why it matters",
      text: "It can help a sound feel more alive, more present, and easier to hear in a mix without relying only on EQ or compression.",
      icon: <AutoAwesomeRoundedIcon sx={{ color: accent }} />,
    },
    {
      title: "Where it is used",
      text: "Vocals, drums, bass, guitars, synths, mix buses, mastering chains, and sound design of almost every kind.",
      icon: <GraphicEqRoundedIcon sx={{ color: accent }} />,
    },
  ];

  const saturationTypes = [
    {
      name: "Tape Saturation",
      description:
        "Often associated with soft compression, rounded transients, subtle harmonic enhancement, and a sense of glue or smoothness.",
    },
    {
      name: "Tube Saturation",
      description:
        "Commonly described as warm, rich, smooth, and harmonically musical, often adding fullness and a slightly soft, flattering character.",
    },
    {
      name: "Transformer / Console Saturation",
      description:
        "Adds density, weight, and subtle color that can help sounds feel more substantial and mix elements feel more cohesive.",
    },
    {
      name: "Transistor Saturation",
      description:
        "Can sound punchy, focused, edgy, or tight depending on the circuit and how hard it is driven.",
    },
    {
      name: "Digital Saturation",
      description:
        "A broad category that can emulate analog behavior or create its own character, from subtle harmonic sweetening to more obvious clipping textures.",
    },
    {
      name: "Multiband Saturation",
      description:
        "Applies different saturation behavior to different frequency ranges, giving more control over warmth, presence, and bite.",
    },
  ];

  const harmonicConcepts = [
    {
      name: "Fundamental",
      description:
        "The original perceived pitch of a sound. Saturation does not usually replace it, but it builds on it.",
    },
    {
      name: "Harmonics",
      description:
        "Additional frequencies related to the fundamental. Saturation adds or emphasizes these harmonics, which changes timbre and character.",
    },
    {
      name: "Even Harmonics",
      description:
        "Often associated with smoother, warmer, sweeter coloration, though the real result depends on the source and circuit behavior.",
    },
    {
      name: "Odd Harmonics",
      description:
        "Often associated with stronger edge, aggression, bite, or intensity, especially as the processing becomes heavier.",
    },
  ];

  const controls = [
    {
      name: "Drive / Input",
      description:
        "Controls how hard the signal hits the saturation stage. More drive usually means more harmonics, more density, and more audible coloration.",
    },
    {
      name: "Output / Level",
      description:
        "Adjusts the signal level after saturation so you can level-match and judge the tonal change more accurately.",
    },
    {
      name: "Mix",
      description:
        "Blends the dry and saturated signals together, which is useful for parallel saturation and preserving transients or clarity.",
    },
    {
      name: "Tone / Bias / Character",
      description:
        "Shapes how the saturation reacts, often affecting brightness, harshness, low-end thickness, symmetry, or overall color.",
    },
    {
      name: "High-Pass / Low-Pass / Emphasis",
      description:
        "Lets you control which frequency ranges hit the saturation stage most strongly, which is extremely useful for tone shaping.",
    },
  ];

  const commonUses = [
    "Making vocals sound warmer, denser, and easier to hear without sounding obviously distorted",
    "Adding bite and presence to bass so it reads better on small speakers",
    "Giving drums more body, smack, and energy",
    "Helping guitars and synths feel more textured or more analog-like",
    "Adding glue and cohesion to buses or full mixes",
    "Creating lo-fi, vintage, gritty, or emotionally charged sound design textures",
  ];

  const bestPractices = [
    "Level-match carefully when judging saturation. Louder almost always sounds better, even when it is not actually better.",
    "Start subtle. Saturation often works best when you feel it before you clearly hear it.",
    "Use EQ before or after saturation to control what frequencies are being emphasized.",
    "Different saturation styles suit different material. Tape, tube, transistor, and digital clipping do not all react the same way.",
    "Check the result in context. A saturated track that sounds impressive in solo can become muddy, harsh, or crowded in the full mix.",
  ];

  const relatedLinks = [
    {
      title: "Distortion",
      to: "/learning/distortion",
      description:
        "Learn how broader distortion reshapes waveforms and creates more obvious harmonic aggression.",
    },
    {
      title: "Overdrive",
      to: "/learning/overdrive",
      description:
        "Explore the smoother, touch-sensitive side of gain and how it differs from heavier processing.",
    },
    {
      title: "Equalization",
      to: "/learning/equalization",
      description:
        "See how EQ works alongside saturation to shape tone, clarity, and balance.",
    },
    {
      title: "Compression",
      to: "/learning/compression",
      description:
        "Understand how saturation and compression often overlap in feel, but not in exactly the same way.",
    },
  ];

  const sectionCardSx = {
    p: 2,
    borderRadius: 3,
    width: "100%",
    overflow: "hidden",
    boxSizing: "border-box",
  };

  return (
    <>
      <Seo
        title="Saturation | Parish Audio Learning"
        description="Learn what saturation is, how it adds harmonics, the difference between tape, tube, transistor, and digital saturation, and how saturation is used in music production and sound design."
        canonical="https://parishaudio.com/learning/saturation"
        schema={articleSchema}
      />

      <Box ref={topContainer}
        sx={{
          width: "95%",
          maxWidth: 1100,
          mx: "auto",
          minHeight: height - 140,
        }}
      >
        <Paper
          sx={{
            mt: 3,
            mb: 4,
            p: { xs: 2, md: 3 },
            background: `linear-gradient(180deg, ${style.color1} 0%, rgba(22,32,48,0.98) 100%)`,
            borderRadius: 3,
            color: style.color4,
            boxShadow: 12,
            overflow: "hidden",
          }}
        >
          <Stack spacing={3.25}>
            <Box>
              <Chip
                label="Learning Center"
                sx={{
                  mb: 1.5,
                  color: style.color4,
                  backgroundColor: "rgba(255,255,255,0.08)",
                  border: "1px solid rgba(255,255,255,0.08)",
                }}
              />

              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: "2rem", md: "3rem" },
                  fontWeight: 800,
                  lineHeight: 1.05,
                  mb: 1.25,
                  color: style.color4,
                  maxWidth: 900,
                }}
              >
                Saturation in Music Production: What It Is and How It Works
              </Typography>

              <Typography
                sx={{
                  color: style.color3,
                  fontSize: { xs: "1rem", md: "1.1rem" },
                  lineHeight: 1.85,
                  maxWidth: 950,
                }}
              >
                Saturation is one of the most widely used forms of tone shaping in
                music production, mixing, and sound design. It can be subtle or
                obvious, smooth or gritty, warm or aggressive, but at its core,
                saturation changes the shape of a signal in a nonlinear way and
                adds harmonic content. That extra harmonic structure is what can
                make a sound feel richer, thicker, more present, or more alive.
              </Typography>

              <Typography
                sx={{
                  color: style.color3,
                  fontSize: { xs: "1rem", md: "1.1rem" },
                  lineHeight: 1.85,
                  mt: 2,
                  maxWidth: 950,
                }}
              >
                In practical terms, saturation is often used to give sterile
                digital recordings more character, help instruments cut through a
                mix, soften transients in a flattering way, or add the feeling of
                analog warmth and density. Sometimes it is barely noticeable on
                its own. Other times it becomes a major part of the sound.
              </Typography>
            </Box>

            <Grid container spacing={2} alignItems="stretch">
              {quickFacts.map((item) => (
                <Grid item xs={12} md={4} key={item.title} sx={{ display: "flex" }}>
                  <Paper
                    sx={{
                      ...sectionCardSx,
                      backgroundColor: panelBg,
                      boxShadow: 6,
                    }}
                  >
                    <Stack direction="row" spacing={1.25} alignItems="flex-start">
                      <Box sx={{ flexShrink: 0 }}>{item.icon}</Box>
                      <Box>
                        <Typography
                          sx={{
                            color: style.color4,
                            fontWeight: 700,
                            mb: 0.5,
                          }}
                        >
                          {item.title}
                        </Typography>
                        <Typography
                          sx={{
                            color: style.color3,
                            lineHeight: 1.7,
                            fontSize: "0.97rem",
                          }}
                        >
                          {item.text}
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>
                </Grid>
              ))}
            </Grid>

            <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />

            <Box>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "1.5rem", md: "2rem" },
                  fontWeight: 800,
                  mb: 1.5,
                  color: style.color4,
                }}
              >
                What is saturation in audio?
              </Typography>

              <Typography
                sx={{
                  color: style.color3,
                  lineHeight: 1.85,
                  mb: 2,
                }}
              >
                Saturation is a type of nonlinear processing that adds harmonics
                and coloration to an audio signal. In many cases, it happens when
                analog hardware or analog-style circuits are driven harder and
                begin to respond less linearly. Instead of perfectly reproducing
                the input, the system gently reshapes the waveform, which changes
                the tone.
              </Typography>

              <Typography
                sx={{
                  color: style.color3,
                  lineHeight: 1.85,
                  mb: 2,
                }}
              >
                Compared with more aggressive distortion, saturation is often more
                subtle and more focused on enhancement than destruction. It may
                add thickness, smooth harsh transients, increase perceived
                loudness, or create the impression of warmth and depth without
                sounding obviously mangled. That is one reason saturation is so
                common across modern mixing workflows.
              </Typography>

              <Typography
                sx={{
                  color: style.color3,
                  lineHeight: 1.85,
                }}
              >
                That said, saturation is still part of the broader distortion
                family. The difference is often a matter of degree, style, and
                intent. Light saturation can be almost invisible. Heavy saturation
                can move toward unmistakable distortion.
              </Typography>
            </Box>

            <Box>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "1.5rem", md: "2rem" },
                  fontWeight: 800,
                  mb: 1.5,
                  color: style.color4,
                }}
              >
                How saturation changes sound
              </Typography>

              <Grid container spacing={2} alignItems="stretch">
                {harmonicConcepts.map((item) => (
                  <Grid item xs={12} md={6} key={item.name} sx={{ display: "flex" }}>
                    <Paper
                      sx={{
                        ...sectionCardSx,
                        backgroundColor: darkPanel,
                      }}
                    >
                      <Typography
                        sx={{
                          color: accent,
                          fontWeight: 700,
                          mb: 0.75,
                        }}
                      >
                        {item.name}
                      </Typography>
                      <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                        {item.description}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>

              <Typography
                sx={{
                  color: style.color3,
                  lineHeight: 1.85,
                  mt: 2,
                }}
              >
                When people say saturation adds warmth or character, they are
                usually describing the audible result of new harmonic content plus
                subtle dynamic shaping. Saturation can make a bass line speak more
                clearly, make a vocal sound more expensive, or make a drum bus
                feel more exciting because the tone becomes denser and more
                complex.
              </Typography>
            </Box>

            <Box>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "1.5rem", md: "2rem" },
                  fontWeight: 800,
                  mb: 1.5,
                  color: style.color4,
                }}
              >
                Common types of saturation
              </Typography>

              <Grid container spacing={2} alignItems="stretch">
                {saturationTypes.map((item) => (
                  <Grid item xs={12} md={6} key={item.name} sx={{ display: "flex" }}>
                    <Paper
                      sx={{
                        ...sectionCardSx,
                        backgroundColor: panelBg,
                        boxShadow: 4,
                      }}
                    >
                      <Typography
                        sx={{
                          color: style.color4,
                          fontWeight: 700,
                          mb: 0.75,
                        }}
                      >
                        {item.name}
                      </Typography>
                      <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                        {item.description}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Box>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "1.5rem", md: "2rem" },
                  fontWeight: 800,
                  mb: 1.5,
                  color: style.color4,
                }}
              >
                Saturation vs distortion
              </Typography>

              <Paper
                sx={{
                  p: 2,
                  borderRadius: 3,
                  backgroundColor: darkPanel,
                  border: "1px solid rgba(255,255,255,0.06)",
                }}
              >
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CompareArrowsRoundedIcon sx={{ color: accent }} />
                    <Typography sx={{ color: style.color4, fontWeight: 700 }}>
                      They are related, but not always used in the same way
                    </Typography>
                  </Stack>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    Saturation is generally treated as the subtler, more
                    enhancement-focused side of distortion. It often aims to add
                    warmth, fullness, density, and harmonic richness without
                    sounding obviously aggressive.
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    Distortion usually implies a more dramatic reshaping of the
                    waveform. It is often harsher, more compressed, more
                    aggressive, and more clearly audible as an effect. The border
                    between the two is not strict, but saturation usually lives
                    closer to coloration, while distortion often lives closer to
                    effect.
                  </Typography>
                </Stack>
              </Paper>
            </Box>

            <Box>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "1.5rem", md: "2rem" },
                  fontWeight: 800,
                  mb: 1.5,
                  color: style.color4,
                }}
              >
                Common saturation controls
              </Typography>

              <Grid container spacing={2} alignItems="stretch">
                {controls.map((item) => (
                  <Grid item xs={12} md={6} key={item.name} sx={{ display: "flex" }}>
                    <Paper
                      sx={{
                        ...sectionCardSx,
                        backgroundColor: panelBg,
                      }}
                    >
                      <Stack direction="row" spacing={1.1} alignItems="flex-start">
                        <TuneRoundedIcon
                          sx={{ color: accent, mt: "2px", flexShrink: 0 }}
                        />
                        <Box>
                          <Typography
                            sx={{
                              color: style.color4,
                              fontWeight: 700,
                              mb: 0.5,
                            }}
                          >
                            {item.name}
                          </Typography>
                          <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                            {item.description}
                          </Typography>
                        </Box>
                      </Stack>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Box>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "1.5rem", md: "2rem" },
                  fontWeight: 800,
                  mb: 1.5,
                  color: style.color4,
                }}
              >
                How saturation is used in mixing and production
              </Typography>

              <Paper
                sx={{
                  p: 2.25,
                  borderRadius: 3,
                  backgroundColor: panelBg,
                }}
              >
                <Stack spacing={1.35}>
                  {commonUses.map((item) => (
                    <Stack
                      key={item}
                      direction="row"
                      spacing={1.1}
                      alignItems="flex-start"
                    >
                      <LibraryMusicRoundedIcon
                        sx={{ color: accent, mt: "2px", fontSize: 20, flexShrink: 0 }}
                      />
                      <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                        {item}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Paper>

              <Typography
                sx={{
                  color: style.color3,
                  lineHeight: 1.85,
                  mt: 2,
                }}
              >
                One of the biggest strengths of saturation is that it can improve
                perceived presence without doing only one thing. EQ changes
                frequency balance. Compression changes dynamics. Saturation can
                affect both timbre and feel at the same time, which is why it is
                so often used as a finishing touch or missing ingredient tool.
              </Typography>
            </Box>

            <Box>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "1.5rem", md: "2rem" },
                  fontWeight: 800,
                  mb: 1.5,
                  color: style.color4,
                }}
              >
                Best practices for using saturation
              </Typography>

              <Grid container spacing={2} alignItems="stretch">
                {bestPractices.map((item, index) => (
                  <Grid item xs={12} md={6} key={index} sx={{ display: "flex" }}>
                    <Paper
                      sx={{
                        ...sectionCardSx,
                        backgroundColor: darkPanel,
                      }}
                    >
                      <Stack direction="row" spacing={1.1} alignItems="flex-start">
                        <TipsAndUpdatesRoundedIcon
                          sx={{ color: accent, mt: "2px", flexShrink: 0 }}
                        />
                        <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                          {item}
                        </Typography>
                      </Stack>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Box>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "1.5rem", md: "2rem" },
                  fontWeight: 800,
                  mb: 1.5,
                  color: style.color4,
                }}
              >
                Is saturation always subtle?
              </Typography>

              <Typography
                sx={{
                  color: style.color3,
                  lineHeight: 1.85,
                  mb: 2,
                }}
              >
                No. Saturation is often subtle in mixing and mastering, but it can
                also be pushed into very obvious coloration. At heavier settings,
                saturation can create aggressive edge, crunch, flattening of peaks,
                and obvious distortion-like textures. This is especially common in
                creative sound design, drum processing, bass enhancement, and
                stylized vocal production.
              </Typography>

              <Typography
                sx={{
                  color: style.color3,
                  lineHeight: 1.85,
                }}
              >
                In other words, saturation is not defined by being quiet or mild.
                It is defined more by the style of nonlinear coloration and the
                way people typically use it. Many tools let you move gradually
                from gentle enhancement to stronger distortion without ever
                leaving the same plugin.
              </Typography>
            </Box>

            <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />

            <Box>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "1.5rem", md: "2rem" },
                  fontWeight: 800,
                  mb: 1.5,
                  color: style.color4,
                }}
              >
                Related learning topics
              </Typography>

              <Grid container spacing={2} alignItems="stretch">
                {relatedLinks.map((item) => (
                  <Grid item xs={12} md={6} key={item.title} sx={{ display: "flex" }}>
                    <Paper
                      sx={{
                        ...sectionCardSx,
                        backgroundColor: panelBg,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                      }}
                    >
                      <Box>
                        <Typography
                          sx={{
                            color: style.color4,
                            fontWeight: 700,
                            mb: 0.75,
                          }}
                        >
                          {item.title}
                        </Typography>
                        <Typography
                          sx={{
                            color: style.color3,
                            lineHeight: 1.75,
                            mb: 2,
                          }}
                        >
                          {item.description}
                        </Typography>
                      </Box>

                      <Box>
                        <Button
                          component={RouterLink}
                          to={item.to}
                          variant="outlined"
                          sx={{
                            color: style.color4,
                            borderColor: "rgba(255,255,255,0.18)",
                            "&:hover": {
                              borderColor: accent,
                              backgroundColor: "rgba(255,255,255,0.04)",
                            },
                          }}
                        >
                          Read More
                        </Button>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />

            <Box>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "1.5rem", md: "2rem" },
                  fontWeight: 800,
                  mb: 1.5,
                  color: style.color4,
                }}
              >
                Final thoughts
              </Typography>

              <Typography
                sx={{
                  color: style.color3,
                  lineHeight: 1.9,
                  mb: 2,
                }}
              >
                Saturation is one of the most useful and musical ways to shape
                tone. It can add warmth, presence, density, glue, edge, or
                excitement depending on how it is applied. Sometimes it solves a
                problem that EQ alone cannot fix. Sometimes it adds a finished,
                analog-like quality that helps a mix feel more polished.
              </Typography>

              <Typography
                sx={{
                  color: style.color3,
                  lineHeight: 1.9,
                }}
              >
                Whether you are working on vocals, guitars, drums, synths, buses,
                or full mixes, learning how saturation behaves is worth the time.
                Used carefully, it can be one of the most powerful tools in music
                production because it changes not only what you hear, but how a
                sound feels.
              </Typography>
            </Box>
          </Stack>
        </Paper>
      </Box>
    </>
  );
}